import type { Metric } from '../api/types';
import { fmtMetricRaw, fmtScore, METRIC_LABELS, scoreTone, toneClass } from '../lib/format';
import { cx } from './ui';

const MIN_CONF = 0.5;

function BandBar({ m }: { m: Metric }) {
  // position of raw value inside [red0, red1], green band highlighted
  const [r0, r1] = m.band.red, [g0, g1] = m.band.green;
  const span = r1 - r0 || 1;
  const pct = (v: number) => Math.max(0, Math.min(100, ((v - r0) / span) * 100));
  return (
    <div className="relative h-1.5 w-full rounded bg-line overflow-hidden">
      <div className="absolute h-full bg-lime/40" style={{ left: `${pct(g0)}%`, width: `${pct(g1) - pct(g0)}%` }} />
      <div className="absolute h-full w-0.5 bg-fog" style={{ left: `calc(${pct(m.rawValue)}% - 1px)` }} />
    </div>
  );
}

/** 'meanNm' → 'mean', 'rmsDevDeg' → 'rms dev': strip a trailing unit token, split camel case. */
const keyLabel = (k: string) => k.replace(/(Nm|Ft|Kt|Deg|M|S)$/, '').replace(/([A-Z])/g, ' $1').toLowerCase().trim();
const detailText = (d: Metric['detail']) => Object.entries(d).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => `${keyLabel(k)} ${v}`).join(' · ');

export default function MetricList({ metrics }: { metrics: Metric[] }) {
  if (!metrics.length) return <div className="text-xs text-dim px-1 py-2">No metrics (segment too short or missing).</div>;
  return (
    <ul className="divide-y divide-line">
      {metrics.map(m => {
        const low = m.confidence < MIN_CONF;
        return (
          <li key={m.name} data-testid={`metric-${m.name}`} className={cx('py-2 px-1', low && 'opacity-50')}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm">{METRIC_LABELS[m.name] ?? m.name}</span>
              <span className="hud text-sm text-dim">{fmtMetricRaw(m.rawValue, m.unit)}</span>
              <span className={cx('hud font-semibold w-8 text-right', toneClass[scoreTone(low ? null : m.score)])}>{fmtScore(m.score)}</span>
            </div>
            <BandBar m={m} />
            <div className="text-[11px] text-dim mt-1">{low ? `low confidence (${Math.round(m.confidence * 100)}%) — excluded from roll-up` : detailText(m.detail)}</div>
          </li>
        );
      })}
    </ul>
  );
}
